import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from './adminApi';
import { Trash2, Search, Mail, Download } from 'lucide-react';

const AdminSubscribers = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    adminApi.listSubscribers().then(setSubs).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter((s) => s.email.toLowerCase().includes(q));
  }, [subs, query]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    await adminApi.deleteSubscriber(id);
    setSubs((prev) => prev.filter((s) => s.id !== id));
  };

  const handleExportCsv = () => {
    const rows = [
      ['email', 'status', 'subscribed_at'],
      ...subs.map((s) => [s.email, s.status, s.created_at || '']),
    ];
    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grouth-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-8 lg:px-12 py-10" data-testid="admin-subscribers-page">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Audience</p>
          <h1 className="text-white text-4xl font-light font-['Inter']">Subscribers</h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-2">
            People who opted in to your insights newsletter.
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={subs.length === 0}
          data-testid="subscribers-export-csv"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </header>

      <div className="relative w-full md:w-80 mb-6">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search emails…"
          data-testid="subscribers-search"
          className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
        />
      </div>

      <div className="bg-[#15151A] border border-white/5 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-white/40 text-sm font-['Lato'] py-16 text-center" data-testid="subscribers-empty">
            {query ? 'No subscribers match your search.' : 'No subscribers yet.'}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead>
              <tr className="text-left text-white/40 text-xs uppercase tracking-wider font-['Inter']">
                <th className="px-6 py-4 font-medium">Email</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Subscribed</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="hover:bg-white/[0.02]">
                  <td className="px-6 py-4 text-white font-['Lato']">
                    <span className="inline-flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-[#83BF42]" />
                      {s.email}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-['Inter'] bg-[#83BF42]/15 text-[#83BF42] border border-[#83BF42]/30">
                      {s.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white/50 text-xs font-['Lato']">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDelete(s.id)}
                      data-testid={`subscriber-delete-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-red-400 text-xs hover:bg-red-500/10 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminSubscribers;
